<template>
  <div class="grading-report-timeline">
    <div class="details md-scrollbar md-theme-default">
      <div class="time-pole" />
      <div v-for="(action, index) in timeline.actions" :key="action._id" class="timeline-action-container">
        <div class="time">
          <div class="top link" />
          <div :ref="`timeline-action-${index}`" class="value">
            {{ timeLabel(action.timeOfAction) }}
          </div>
          <div class="bottom link" />
        </div>
        <timeline-action :action="action" :report="report" />
      </div>
    </div>
    <div class="overview">
      <div class="time-bar">
        <div
          v-for="tickMark in tickMarks"
          :key="tickMark"
          class="tickmark"
          :style="{ top: `${(tickMark / timeline.duration) * 100}%` }"
        >
          <span class="tickmark-value">
            {{ timeLabel(tickMark) }}
          </span>
        </div>
        <div
          v-for="(action, index) in timeline.actions"
          :key="action._id"
          :class="`action ${actionClasses(action)}`"
          :style="{
            top: `${(action.timeOfAction / timeline.duration) * 100}%`,
            zIndex: timeline.actions.length - index,
          }"
          @click="scrollToAction(action)"
        >
          <md-icon>
            {{ ACTION_TYPE_ICONS[action.type] || "directions_run" }}
          </md-icon>
          <div class="arrow-left" />
          <md-tooltip md-direction="right">
            {{ timeLabel(action.timeOfAction) }}
            {{ overviewLabel(action) }}
          </md-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import range from 'lodash/range';
import { categoryLabel, categoryVerb } from '~lib/common/utils/gradingReports';

import ACTION_TYPE_ICONS from './actionTypeIcons';

import TimelineAction from './TimelineAction.vue';

import getGradingActionFromTimelineAction from './getGradingActionFromTimelineAction';

export default {
  name: 'grading-report-timeline',
  components: {
    TimelineAction,
  },
  props: {
    report: {
      type: Object,
      required: true,
    },
    timeline: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      ACTION_TYPE_ICONS,
    };
  },
  computed: {
    tickMarks() {
      return range(0, this.timeline.duration, 60).concat(
        this.timeline.duration % 60 !== 0 ? this.timeline.duration : []
      );
    },
  },
  methods: {
    timeLabel(tickMark) {
      return `${Math.floor(tickMark / 60)}:${(tickMark % 60).toLocaleString(
        'en-US',
        {
          minimumIntegerDigits: 2,
        }
      )}`;
    },
    overviewLabel(action) {
      return categoryLabel(action) + ' ' + categoryVerb(action);
    },
    actionClasses(action) {
      const { report } = this;

      const gradingAction = getGradingActionFromTimelineAction(report, action)
      if (!gradingAction) {
        return;
      }
      return `${gradingAction.isSuccess ? 'good' : ''} ${gradingAction.isFailure ? 'bad' : ''} ${
        gradingAction.isCritical ? 'critical' : ''
      }`;
    },
    scrollToAction(action) {
      const actionIndex = this.timeline.actions.indexOf(action);
      const timelineActionRef = this.$refs[`timeline-action-${actionIndex}`];
      if (!timelineActionRef || !timelineActionRef[0]) {
        return;
      }
      const timelineActionEl = timelineActionRef[0];
      if (!timelineActionEl) {
        return;
      }
      timelineActionEl.scrollIntoView({
        behavior: 'smooth'
      });
    },
  },
};
</script>

<style scoped>
.grading-report-timeline {
  height: 100%;
  padding-top: 30px;
  overflow: auto;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  /* Spacing to prevent the last action from overflowing if it happens right at case end */
  padding-bottom: 32px;
}

.overview, .details {
  height: 100%;
}

.overview {
  width: 80px;
  margin-left: 50px;
}

.details {
  flex: 1;
  overflow: auto;
  overflow-x: hidden;
  padding: 0 10px;
  padding-bottom: 10px;
}

.overview .time-bar {
  height: 100%;
  width: 5px;
  background-color: #707070;
  position: relative;
}

.tickmark {
  position: absolute;
  width: 25px;
  left: -20px;
  height: 5px;
  background-color: #707070;
  transform: translateY(-5px);
}

.tickmark-value {
  position: absolute;
  top: -20px;
  left: -20px;
}

.action {
  position: absolute;
  transform: translateY(-12px) translateX(10px);
  background-color: #707070;
  color: white;
  height: 32px;
  width: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform linear 0.2s;
  cursor: pointer;
}

.action.good {
  background-color: #62cd63;
}

.action.bad {
  background-color: #db4437;
}

.action.critical::after {
  content: "!";
  position: absolute;
  right: -8px;
  width: 6px;
  height: 28px;
  font-family: Roboto;
  font-size: 23px;
  font-weight: bold;
  line-height: normal;
}

.action.good.critical::after {
  color: #62cd63;
}

.action.bad.critical::after {
  color: #db4437;
}

.action:hover {
  transform: scale(1.2) translateY(-10px) translateX(20px);
  z-index: 1000 !important;
}

.md-icon {
  color: white !important;
}

.arrow-left {
  width: 0;
  height: 0;
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;

  border-right: 10px solid #707070;
  position: absolute;
  left: -5px;
}

.action.good .arrow-left {
  border-right-color: #62cd63;
}

.action.bad .arrow-left {
  border-right-color: #db4437;
}

.timeline-action-container {
  display: flex;
  flex-direction: column;
}

.details {
  position: relative;
  transform: translate3d(0,0,0);
}

.details .time {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.details .time .value {
  color: #fff;
  background-color: #383838;
  margin: auto;
  font-size: 18px;
  border-radius: 4px;
  padding: 4px 10px;
}

.details .time .link {
  background-color: #707070;
  height: 15px;
  width: 5px;
  z-index: -1;
}

</style>